// ============================================================
// Theme Configuration
// Central source of truth for all design tokens and variables.
// Import from here when you need consistent values in components.
// ============================================================

export const theme = {
  // ── Colors ──────────────────────────────────────────────
  colors: {
    // Brand — Orange (#F7941D)
    primary: {
      50: '#fff8f0',
      100: '#ffedd5',
      200: '#fed7aa',
      300: '#fdba74',
      400: '#fb923c',
      500: '#F7941D',
      600: '#D97B06',
      700: '#c2410c',
      800: '#9a3412',
      900: '#7c2d12',
      950: '#431407',
    },
    secondary: {
      50: '#f0fdf4',
      100: '#dcfce7',
      200: '#bbf7d0',
      300: '#86efac',
      400: '#4ade80',
      500: '#22c55e',
      600: '#16a34a',
      700: '#15803d',
      800: '#166534',
      900: '#14532d',
      950: '#052e16',
    },

    // Neutrals
    neutral: {
      0: '#ffffff',
      50: '#f9fafb',
      100: '#f3f4f6',
      200: '#e5e7eb',
      300: '#d1d5db',
      400: '#9ca3af',
      500: '#6b7280',
      600: '#4b5563',
      700: '#374151',
      800: '#1f2937',
      900: '#111827',
      950: '#030712',
    },

    // Semantic / Status
    success: {
      light: '#dcfce7',
      DEFAULT: '#22c55e',
      dark: '#15803d',
    },
    warning: {
      light: '#fef9c3',
      DEFAULT: '#eab308',
      dark: '#a16207',
    },
    error: {
      light: '#fee2e2',
      DEFAULT: '#ef4444',
      dark: '#b91c1c',
    },
    info: {
      light: '#dbeafe',
      DEFAULT: '#3b82f6',
      dark: '#1d4ed8',
    },

    // Surface & Background
    background: {
      page: '#F7F8FA',
      card: '#ffffff',
      sidebar: '#0F1B2D',
      overlay: 'rgba(0, 0, 0, 0.5)',
      hero: '#111827',
    },

    // Sidebar
    sidebar: {
      bg: '#0F1B2D',
      border: '#1A2A40',
      text: {
        active: '#F7941D',
        default: '#d1d5db', // gray-300
        hover: '#ffffff',
        subDefault: '#9ca3af', // gray-400
        sectionHeader: '#6b7280', // gray-500
      },
      hoverBg: 'rgba(255, 255, 255, 0.1)', // white/10
      chevron: '#6b7280', // gray-500
    },

    // Header
    header: {
      bg: '#ffffff',
      border: '#e5e7eb', // gray-200
      iconDefault: '#6b7280', // gray-500
      iconMuted: '#9ca3af', // gray-400
      iconHover: '#4b5563', // gray-600
      createButton: '#2563eb', // blue-600
      createButtonHover: '#1d4ed8', // blue-700
      notificationDot: '#2563eb', // blue-600
    },

    // Assistant Panel
    assistant: {
      optionBorderHover: '#F7941D',
      optionBgHover: '#fff7ed', // orange-50
      inputFocusRing: '#F7941D',
    },

    // Text
    text: {
      primary: '#111827',
      secondary: '#4b5563',
      tertiary: '#9ca3af',
      inverse: '#ffffff',
      link: '#F7941D',
      linkHover: '#D97B06',
    },

    // Borders
    border: {
      light: '#e5e7eb',
      DEFAULT: '#d1d5db',
      dark: '#9ca3af',
      focus: '#F7941D',
    },
  },

  // ── Brand Shorthand ─────────────────────────────────────
  // Quick access to the most commonly used brand values
  brand: {
    primary: '#F7941D',
    primaryHover: '#D97B06',
    primaryLight: '#F7941D1A', // 10% opacity
    primarySubtle: '#F7941D08', // 3% opacity
    primaryRing: '#F7941D33', // 20% opacity
    primaryRingActive: '#F7941D4D', // 30% opacity
  },

  // ── Typography ──────────────────────────────────────────
  typography: {
    fontFamily: {
      heading: ['Inter', 'system-ui', 'sans-serif'],
      body: ['Inter', 'system-ui', 'sans-serif'],
      mono: ['JetBrains Mono', 'Fira Code', 'monospace'],
    },
    fontSize: {
      xs: '0.75rem', // 12px
      sm: '0.875rem', // 14px
      base: '1rem', // 16px
      lg: '1.125rem', // 18px
      xl: '1.25rem', // 20px
      '2xl': '1.5rem', // 24px
      '3xl': '1.875rem', // 30px
      '4xl': '2.25rem', // 36px
    },
    fontWeight: {
      normal: '400',
      medium: '500',
      semibold: '600',
      bold: '700',
    },
    lineHeight: {
      tight: '1.25',
      normal: '1.5',
      relaxed: '1.75',
    },
  },

  // ── Spacing ─────────────────────────────────────────────
  spacing: {
    px: '1px',
    0: '0',
    0.5: '0.125rem', // 2px
    1: '0.25rem', // 4px
    1.5: '0.375rem', // 6px
    2: '0.5rem', // 8px
    3: '0.75rem', // 12px
    4: '1rem', // 16px
    5: '1.25rem', // 20px
    6: '1.5rem', // 24px
    8: '2rem', // 32px
    10: '2.5rem', // 40px
    12: '3rem', // 48px
    16: '4rem', // 64px
    20: '5rem', // 80px
    24: '6rem', // 96px
  },

  // ── Border Radius ───────────────────────────────────────
  borderRadius: {
    none: '0',
    sm: '0.25rem', // 4px
    DEFAULT: '0.375rem', // 6px
    md: '0.5rem', // 8px
    lg: '0.75rem', // 12px
    xl: '1rem', // 16px
    '2xl': '1.5rem', // 24px
    full: '9999px',
  },

  // ── Shadows ─────────────────────────────────────────────
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    DEFAULT:
      '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
    none: 'none',
  },

  // ── Transitions ─────────────────────────────────────────
  transitions: {
    fast: '150ms ease',
    DEFAULT: '200ms ease',
    slow: '300ms ease',
    spring: '500ms cubic-bezier(0.34, 1.56, 0.64, 1)',
  },

  // ── Z-Index ─────────────────────────────────────────────
  zIndex: {
    dropdown: 10,
    sticky: 20,
    overlay: 30,
    modal: 40,
    popover: 50,
    toast: 60,
    tooltip: 70,
  },

  // ── Breakpoints ─────────────────────────────────────────
  breakpoints: {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
  },

  // ── Layout ──────────────────────────────────────────────
  layout: {
    sidebar: {
      width: '15rem', // w-60 = 240px
      collapsedWidth: '64px',
    },
    header: {
      height: '3.5rem', // h-14 = 56px
    },
    content: {
      maxWidth: '1280px',
      padding: '1.5rem',
    },
    auth: {
      maxWidth: '28rem',
      imagePanelWidth: '50%',
    },
  },
} as const

// ── Type Exports ────────────────────────────────────────
export type Theme = typeof theme
export type ThemeColors = typeof theme.colors
export type ThemeTypography = typeof theme.typography
export type ThemeBrand = typeof theme.brand
export type ThemeSidebar = typeof theme.colors.sidebar
export type ThemeHeader = typeof theme.colors.header
